import React, { useState, useMemo } from "react";
import { loadFromLocalStorage, setOnLocalStorage } from "state/utils";
import { CONFIG_LAST_UPDATED } from "resources/constants";
import useConfig from "state/config";
import { useEffect } from "react";
import useI18n from "state/i18n";
import { ALERT_TYPES, useAlert } from '@etiquette-ui/alert-dialog';
import { PromoModal, MenuPickerModal, LocationPickerModal } from "./Dialogs";

const MILLIS_IN_HOUR = 1000 * 60 * 60;

const getLastUpdated = (lastUpdated) => (!!lastUpdated ? lastUpdated : 0);

const shouldBeOpened = () => {
  const lastUpdated = loadFromLocalStorage(CONFIG_LAST_UPDATED);
  return Date.now() > getLastUpdated(lastUpdated) + MILLIS_IN_HOUR * 12;
};

export default function ConfigDialog() {  
  const alert = useAlert();
  const [alreadyOpenedOnce, setAlreadyOpenedOnce] = useState(false);
  const [config] = useConfig();
  const [i18n] = useI18n();

  const locationPickerConfig = useMemo(()=>config["location-picker"]?.payload, [config]);
  const menuPickerConfig = useMemo(()=>config["menu-picker"]?.payload, [config]);
  const promotionConfig = useMemo(()=>config.promotion?.payload, [config]);

  const locationPickerEnabled = useMemo(() => {
    if (!locationPickerConfig?.active) return false;
    return true;
  }, [locationPickerConfig]);

  const menuPickerEnabled = useMemo(() => {
    if (!menuPickerConfig?.active) return false;
    return true;
  }, [menuPickerConfig]);

  const promoEnabled = useMemo(() => {
    if (!promotionConfig?.active) return false;
    return shouldBeOpened();
  }, [promotionConfig]);

  function handleInitialModal(locationPickerEnabled, menuPickerEnabled, promoEnabled) {
    if (locationPickerEnabled) {
      alert({
        type: ALERT_TYPES.CUSTOM,
        config: {
          body: LocationPickerModal,
          props: {
            lang: i18n.lang,
            locationPickerConfig,
            handleLocationChange,
          },
        },
      }).then(()=>{
        closeLocationPicker();
      }).catch(()=>{
        closeLocationPicker();
      });
    } else if (menuPickerEnabled) {
      openMenuPickerModal()
    } else if (promoEnabled) {
      openPromoModal();
    }
  }

  useEffect(() => {
    if (alreadyOpenedOnce) return;
    handleInitialModal(locationPickerEnabled, menuPickerEnabled, promoEnabled);
  }, [locationPickerEnabled, menuPickerEnabled, promoEnabled, alreadyOpenedOnce]);

  function closeLocationPicker() {
    if (menuPickerEnabled) {
      openMenuPickerModal();
      return;
    }
    if (promoEnabled) {
      openPromoModal();
      return;
    }
    setAlreadyOpenedOnce(true);
  }

  const openMenuPickerModal = () => {
    alert({
      type: ALERT_TYPES.CUSTOM,
      config: {
        body: MenuPickerModal,
        props: {
          lang: i18n.lang,
          menuPickerConfig,
          buttonLabel: i18n.tag("DINE_IN"),
          closeMenuPicker: ()=>{}
        },
      },
    }).then(()=>{
      closeMenuPicker();
    }).catch(()=>{
      closeMenuPicker();
    });
  }

  const openPromoModal = () => {
    alert({
      type: ALERT_TYPES.CUSTOM,
      config: {
        body: PromoModal,
        props: {
          promotionConfig,
          closePromo : ()=>{},
        },
      },
    }).then(()=>{
      setOnLocalStorage(CONFIG_LAST_UPDATED, Date.now());
      setAlreadyOpenedOnce(true);
    }).catch(()=>{
      setOnLocalStorage(CONFIG_LAST_UPDATED, Date.now());
      setAlreadyOpenedOnce(true);
    });
  }

  function closeMenuPicker() {
    if (promoEnabled) {
      openPromoModal();
      return;
    }
    setAlreadyOpenedOnce(true);
  }

  function handleLocationChange(url) {
    if(!!url) {
      window.location.href = url;
      return;
    }
    closeLocationPicker();
  }
  return (<></>);
}